import { DateTime } from 'luxon';

import { toPlainIfImmutable } from '../../utils/core/immutableHelpers';
import type { RecurrenceType } from './customFrequencyTypes';
import {
  convertClientAliasToQCSAlias,
  mapRecurrenceFrequencyToFrequencyAlias,
  recurrenceFromVirtualRecurrence,
  recurrenceInfo,
} from './customFrequencyUtils';
import { AliasEnum } from './frequency';

export type SeriesDateValue = DateTime | string | null | undefined;

export const toSeriesDateTime = (value: SeriesDateValue): DateTime | undefined => {
  if (!value) return undefined;
  const date = value instanceof DateTime ? value : DateTime.fromISO(value.toString());
  return date.isValid ? date : undefined;
};

export const getSeriesFrequencyCaption = ({
  recurrence,
  startDate,
  endDate,
}: {
  recurrence?: RecurrenceType | null;
  startDate: SeriesDateValue;
  endDate?: SeriesDateValue;
}): string => {
  const normalizedStartDate = toSeriesDateTime(startDate);
  if (!recurrence || !normalizedStartDate) return '';

  return (
    recurrenceInfo({
      recurrence,
      startDate: normalizedStartDate,
      endDate: toSeriesDateTime(endDate),
    }).text || ''
  );
};

/** The recurring-upsert form fields the frequency sentence and next due date are derived from. */
export type SeriesFrequencyFormValues = {
  virtualRecurrence: RecurrenceType;
  virtualFrequency: string;
  dueOn?: SeriesDateValue;
  endOn?: SeriesDateValue;
};

const getFormRecurrence = (values: SeriesFrequencyFormValues): RecurrenceType =>
  recurrenceFromVirtualRecurrence(toPlainIfImmutable(values.virtualRecurrence) ?? {});

/**
 * The full form-values-to-frequency-sentence pipeline. Single composition
 * point for every surface that captions a series from its form state.
 */
export const getSeriesFormFrequencyCaption = (values: SeriesFrequencyFormValues): string =>
  getSeriesFrequencyCaption({
    recurrence: getFormRecurrence(values),
    startDate: values.dueOn,
    endDate: values.endOn,
  });

/**
 * One-time payments and multiple fixed dates carry their own dates, so the
 * editors hide the next due date, end date and payment list for them. Every
 * other pattern repeats from a start date.
 */
export const repeatsFromStartDate = (alias: string | null | undefined): boolean => {
  if (!alias) return true;
  const qcsAlias = convertClientAliasToQCSAlias(alias);
  return qcsAlias !== AliasEnum.ONE_TIME_PAYMENT && qcsAlias !== AliasEnum.MULTIPLE_FIXED_DATES;
};

export type RecurrencePreview = {
  /** Summary sentence, e.g. "Repeats every month on the 15th."; empty while the recurrence is incomplete. */
  text: string;
  /** Up to six upcoming payments for patterns that repeat from the start date; empty otherwise. */
  paymentDates: DateTime[];
  /**
   * The next due date the editors show and save: the first payment on or after the start date, the next
   * fixed date, or the one-time date. Undefined while the recurrence or the start date is incomplete.
   */
  nextDueOn: DateTime | undefined;
};

/**
 * The editors' single rule for the summary sentence, the payment list and the
 * derived next due date. Occurrences count from the start date: an off-pattern
 * date resolves to the next occurrence, and for an every-2-weeks or
 * every-2-months pattern a picked date also sets the phase. QCS keeps such a
 * date on save, and moves any other off-pattern dueOn to its next occurrence.
 */
export const getRecurrencePreview = ({
  recurrence,
  startDate,
  endDate,
}: {
  recurrence: RecurrenceType | null | undefined;
  startDate: SeriesDateValue;
  endDate?: SeriesDateValue;
}): RecurrencePreview => {
  const start = toSeriesDateTime(startDate);
  if (!recurrence || !start) return { text: '', paymentDates: [], nextDueOn: undefined };

  const { text, nextDates } = recurrenceInfo({ recurrence, startDate: start, endDate: toSeriesDateTime(endDate) });
  const dates = nextDates.map((date) => DateTime.fromJSDate(date).startOf('day'));
  return {
    text,
    paymentDates: repeatsFromStartDate(mapRecurrenceFrequencyToFrequencyAlias(recurrence)) ? dates : [],
    nextDueOn: dates[0],
  };
};

/**
 * The next due date a series editor shows: the first occurrence on or after the start date (the stored due date,
 * or the date the user last picked), else the stored date. Until an edit writes it, an off-pattern stored date is
 * only displayed this way; QCS moves it there when the series is saved.
 */
export const getSeriesNextDueOn = ({
  recurrence,
  dueOn,
  startDate = dueOn,
  endDate,
}: {
  recurrence: RecurrenceType | null | undefined;
  dueOn: SeriesDateValue;
  startDate?: SeriesDateValue;
  endDate?: SeriesDateValue;
}): DateTime | undefined =>
  getRecurrencePreview({ recurrence, startDate, endDate }).nextDueOn ?? toSeriesDateTime(dueOn);

/** {@link getSeriesNextDueOn} for the recurring-upsert form, from the same fields as its frequency sentence. */
export const getSeriesFormNextDueOn = (values: SeriesFrequencyFormValues): DateTime | undefined =>
  getSeriesNextDueOn({ recurrence: getFormRecurrence(values), dueOn: values.dueOn, endDate: values.endOn });

/** Heading for the payment list, e.g. "Next 6 payments". */
export const getPaymentPreviewHeading = (count: number): string => `Next ${count} payment${count === 1 ? '' : 's'}`;
